import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './EnterpriseAuthContext';
import { getUserUsage, hasReachedLimit, isApproachingLimit } from '../services/usageService';

// Initial state
const initialState = {
  usageData: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

// Create context
const UsageContext = createContext();

// Custom hook to use the context
export const useUsage = () => {
  const context = useContext(UsageContext);
  if (!context) {
    throw new Error('useUsage must be used within a UsageProvider');
  }
  return context;
};

// Provider component
export const UsageProvider = ({ children }) => {
  const { state: authState, actions: authActions } = useAuth();
  const [state, setState] = useState(initialState);

  // Update state helper
  const updateState = useCallback((updates) => {
    setState(prevState => ({ ...prevState, ...updates }));
  }, []);

  // Load usage data. Returns usageData on success, null on skip/error.
  const loadUsageData = useCallback(async (forceRefresh = false) => {
    // Don't load if user is not authenticated or no active account
    if (!authState.user || !authState.activeAccount || !authState.isAuthenticated) {
      return null;
    }

    // Don't reload if data is fresh (less than 1 minute old) unless forced
    if (!forceRefresh && state.usageData && state.lastUpdated) {
      const timeSinceUpdate = Date.now() - state.lastUpdated;
      const oneMinute = 60 * 1000;
      if (timeSinceUpdate < oneMinute) {
        return state.usageData;
      }
    }

    updateState({ loading: true, error: null });

    try {
      const usageData = await getUserUsage(
        authState.user.id,
        authState.activeAccount.id,
        authState.currentMembership,
        authActions.isAccountAdmin()
      );

      updateState({
        usageData,
        loading: false,
        lastUpdated: Date.now(),
      });
      return usageData;
    } catch (error) {
      console.error('Error loading usage data:', error);
      updateState({
        error: error.message || 'Failed to load usage data',
        loading: false,
      });
      return null;
    }
  }, [
    authState.user,
    authState.activeAccount,
    authState.currentMembership,
    authState.isAuthenticated,
    authActions,
    state.usageData,
    state.lastUpdated,
    updateState,
  ]);

  // Load usage data when auth state changes
  useEffect(() => {
    if (authState.isAuthenticated && authState.user && authState.activeAccount) {
      loadUsageData();
    } else {
      // Clear usage data when user logs out or switches accounts
      setState(initialState);
    }
  }, [authState.isAuthenticated, authState.user, authState.activeAccount, loadUsageData]);

  // Refresh usage data (bypasses cache). Returns fresh usageData for immediate limit checks.
  const refreshUsage = useCallback(() => {
    return loadUsageData(true);
  }, [loadUsageData]);

  // Clear error
  const clearError = useCallback(() => {
    updateState({ error: null });
  }, [updateState]);

  // Utility functions
  const hasReachedTemplateLimit = useCallback(() => {
    return state.usageData ? hasReachedLimit(state.usageData, 'templates') : false;
  }, [state.usageData]);

  const hasReachedSessionLimit = useCallback(() => {
    return state.usageData ? hasReachedLimit(state.usageData, 'sessions') : false;
  }, [state.usageData]);

  const hasReachedDocumentLimit = useCallback(() => {
    return state.usageData ? hasReachedLimit(state.usageData, 'documents') : false;
  }, [state.usageData]);

  const isApproachingTemplateLimit = useCallback(() => {
    return state.usageData ? isApproachingLimit(state.usageData, 'templates') : false;
  }, [state.usageData]);

  const isApproachingSessionLimit = useCallback(() => {
    return state.usageData ? isApproachingLimit(state.usageData, 'sessions') : false;
  }, [state.usageData]);

  const isApproachingDocumentLimit = useCallback(() => {
    return state.usageData ? isApproachingLimit(state.usageData, 'documents') : false;
  }, [state.usageData]);

  // Get usage percentage with color coding
  const getUsageColor = useCallback((type) => {
    if (!state.usageData) return 'default';
    
    const usage = state.usageData.usage[type];
    if (!usage || usage.limit === Infinity) return 'primary';
    
    if (usage.percentage >= 100) return 'error';
    if (usage.percentage >= 80) return 'warning';
    if (usage.percentage >= 60) return 'info';
    return 'success';
  }, [state.usageData]);

  // Format limit display
  const formatLimit = useCallback((limit) => {
    return limit === Infinity ? '∞' : limit.toString();
  }, []);

  const contextValue = useMemo(() => ({
    // State
    ...state,
    
    // Actions
    refreshUsage,
    clearError,
    
    // Utility functions
    hasReachedTemplateLimit,
    hasReachedSessionLimit,
    hasReachedDocumentLimit,
    isApproachingTemplateLimit,
    isApproachingSessionLimit,
    isApproachingDocumentLimit,
    getUsageColor,
    formatLimit,
  }), [
    state,
    refreshUsage,
    clearError,
    hasReachedTemplateLimit,
    hasReachedSessionLimit,
    hasReachedDocumentLimit,
    isApproachingTemplateLimit,
    isApproachingSessionLimit,
    isApproachingDocumentLimit,
    getUsageColor,
    formatLimit,
  ]);

  return (
    <UsageContext.Provider value={contextValue}>
      {children}
    </UsageContext.Provider>
  );
};

export default UsageContext; 